import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Fade, Zoom } from '@mui/material';
import { useSecurityContext } from '../../security/hook/useSecurityContext';
import { useNumericInput } from '../../shared/hook/useNumericInput';
import { useAuthUserAPI } from '../../api/auth/useAuthUserAPI';
import LoginForm from './LoginForm';
import { AuthenticatedUser } from '../../security/type/User';
import { countryPhoneCodes } from '../../shared/util/phoneUtil';
import loginImage from '../../static/app_logo.png';
import './login.css';

const LoginPage: FC = () => {
    const navigate = useNavigate();
    const { authUser } = useAuthUserAPI();
    const { setUser, verificationCodeInputVisible } = useSecurityContext();

    const [selectedPrefix, setSelectedPrefix] = useState('+373');
    const [phoneNumberProps] = useNumericInput('');
    const [verificationCodeProps] = useNumericInput('');

    const handlePrefixChange = (value: string) => {
        setSelectedPrefix(value);
    };

    const handleSubmitPhoneNumber = async (
        event: React.FormEvent<HTMLFormElement>
    ): Promise<void> => {
        event.preventDefault();
        const fullPhoneNumber = `${selectedPrefix}${phoneNumberProps.value}`;
        await authUser(fullPhoneNumber, '');
    };

    const handleSubmitCode = async (
        event: React.FormEvent<HTMLFormElement>
    ): Promise<void> => {
        event.preventDefault();
        const fullPhoneNumber = `${selectedPrefix}${phoneNumberProps.value}`;
        const userData: AuthenticatedUser = await authUser(
            fullPhoneNumber,
            verificationCodeProps.value
        );

        setUser(userData);
        navigate('/');
    };

    return (
        <Box className="iauto-login-page-wrapper">
            <Container maxWidth="sm">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                    }}
                >
                    <Fade in={true} timeout={800}>
                        <Paper
                            elevation={24}
                            sx={{
                                width: '100%',
                                maxWidth: 480,
                                borderRadius: 4,
                                overflow: 'hidden',
                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                backdropFilter: 'blur(20px)',
                            }}
                        >
                            <Box
                                sx={{
                                    px: { xs: 3, sm: 6 },
                                    py: { xs: 4, sm: 6 },
                                }}
                            >
                                {/* Logo */}
                                <Zoom in={true} timeout={600}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mb: 4,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={loginImage}
                                            alt="Logo"
                                            sx={{
                                                maxWidth: '200px',
                                                width: '100%',
                                                height: 'auto',
                                            }}
                                        />
                                    </Box>
                                </Zoom>

                                {/* Login Form */}
                                <LoginForm
                                    onSubmit={
                                        verificationCodeInputVisible
                                            ? handleSubmitCode
                                            : handleSubmitPhoneNumber
                                    }
                                    inputProps={
                                        verificationCodeInputVisible
                                            ? verificationCodeProps
                                            : phoneNumberProps
                                    }
                                    verificationCodeVisible={verificationCodeInputVisible}
                                    countryPhoneCodes={countryPhoneCodes}
                                    selectedPrefix={selectedPrefix}
                                    onPrefixChange={handlePrefixChange}
                                />
                            </Box>
                        </Paper>
                    </Fade>
                </Box>
            </Container>
        </Box>
    );
};

export default LoginPage;
